import { Link } from 'react-router-dom';
import {
  Smartphone,
  Bell,
  Camera,
  MessageSquareReply,
  Wifi,
  ShieldCheck,
  Zap,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import PairPhonePanel from '../components/PairPhonePanel';

/**
 * Marketing landing page for the Supplier Wingman PWA.
 *
 * Two states:
 *   - Logged-out / non-supplier: explains what Wingman is, encourages sign-up
 *   - Logged-in supplier: shows the QR pair card right on the page so they
 *     can pair their phone in one trip without hunting through the dashboard
 */
export default function SupplierAppPage() {
  const { user } = useAuth();
  const isSupplier = user?.user_type === 'supplier';

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="supplier-app-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center mb-20">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3">Supplier Wingman</p>
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-foreground leading-tight mb-6">
              Run your yard
              <br />
              <span className="text-accent">from your pocket.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed mb-8">
              The Supplier Wingman PWA puts your PatinaMart inbox, instant enquiry alerts, and
              one-tap listing capture on the phone you already use. Pair once with a QR code —
              stays signed in for 30 days.
            </p>
            <div className="flex flex-wrap gap-3">
              {isSupplier ? (
                <a href="#pair" className="inline-flex">
                  <Button className="btn-gold" data-testid="supplier-app-pair-cta">
                    Pair my phone <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              ) : (
                <>
                  <Link to="/auth?mode=register&type=supplier">
                    <Button className="btn-gold" data-testid="supplier-app-signup-cta">
                      Become a supplier <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/about">
                    <Button variant="outline" className="border-border/40">
                      How PatinaMart works
                    </Button>
                  </Link>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-6 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Free for verified suppliers • No app store required • Works on any modern phone
            </p>
          </div>

          {/* Phone-shaped preview */}
          <div className="flex justify-center lg:justify-end">
            <PhonePreview />
          </div>
        </div>

        {/* Feature grid */}
        <div className="mb-20">
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
            Built for the way salvage yards actually work.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              icon={Bell}
              title="Real-time enquiry alerts"
              body="The instant a buyer enquires, your phone buzzes. Reply in two taps from the same screen — no email tab swap."
              testId="feature-alerts"
            />
            <FeatureCard
              icon={Camera}
              title="One-tap quick listing"
              body="Snap a photo of new stock, type a title, set a price. The full draft lands in your dashboard ready to publish."
              testId="feature-quickadd"
            />
            <FeatureCard
              icon={MessageSquareReply}
              title="Reply on the move"
              body="The Inbox tab keeps every pending enquiry in one place. Mark replied with a tap so nothing slips through."
              testId="feature-replies"
            />
            <FeatureCard
              icon={Smartphone}
              title="Install like a real app"
              body="Add Wingman to your home screen from your browser menu — opens fullscreen, no toolbar, no fuss."
              testId="feature-pwa"
            />
            <FeatureCard
              icon={Wifi}
              title="Offline-tolerant"
              body="Compose enquiry replies and capture listings even on patchy yard wi-fi. They sync the moment you're back online."
              testId="feature-offline"
            />
            <FeatureCard
              icon={Zap}
              title="Pair once, work for 30 days"
              body="Scan the QR on your dashboard with your phone camera. No passwords. Stays paired for 30 days."
              testId="feature-pair"
            />
          </div>
        </div>

        {/* How it works */}
        <div className="mb-20 rounded-xl border border-border/20 bg-card/30 p-8 md:p-10">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3">How pairing works</p>
          <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-8">
            From zero to paired in 30 seconds.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Step
              n={1}
              title="Open your dashboard"
              body="On any laptop, sign in to PatinaMart and head to Supplier Dashboard → Pair my phone."
            />
            <Step
              n={2}
              title="Scan the QR with your phone camera"
              body="Or punch the 6-digit code into your phone if you'd rather. Codes expire after 5 minutes for safety."
            />
            <Step
              n={3}
              title="You're in"
              body="Your phone is signed in. Add Wingman to your home screen and you've got a one-tap supplier app."
            />
          </div>
        </div>

        {/* Pair card (suppliers only) or call-to-action */}
        {isSupplier ? (
          <div id="pair" className="mb-12 scroll-mt-32">
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3">Pair your phone now</p>
            <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
              Scan with your phone camera.
            </h3>
            <PairPhonePanel />
          </div>
        ) : (
          <div className="text-center rounded-xl border border-accent/30 bg-accent/5 p-10 md:p-14">
            <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-3">
              Got a salvage yard? Wingman comes with the territory.
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
              Sign up as a supplier on PatinaMart and Wingman is yours from day one — no extra
              install, no extra cost, no app store hoops.
            </p>
            <Link to="/auth?mode=register&type=supplier">
              <Button className="btn-gold" data-testid="supplier-app-signup-bottom-cta">
                Become a supplier <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const FeatureCard = ({ icon: Icon, title, body, testId }) => (
  <div
    className="rounded-xl border border-border/20 bg-card/40 p-6 hover:border-accent/30 transition-colors"
    data-testid={testId}
  >
    <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-accent" />
    </div>
    <h3 className="font-medium text-foreground mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

const Step = ({ n, title, body }) => (
  <div>
    <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center text-accent font-semibold mb-3">
      {n}
    </div>
    <h4 className="font-medium text-foreground mb-1.5">{title}</h4>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

/** A flat, drawn phone illustration mirroring the actual Wingman shell. */
const PhonePreview = () => (
  <div
    className="relative w-[260px] h-[520px] rounded-[36px] border-[6px] border-slate-800 bg-[#0F172A] shadow-2xl shadow-black/50 overflow-hidden"
    data-testid="supplier-app-phone-preview"
  >
    <div className="absolute top-0 inset-x-0 h-7 bg-[#0F172A] flex justify-center items-end pb-1">
      <div className="w-20 h-[18px] rounded-b-2xl bg-slate-800" />
    </div>
    <div className="px-4 pt-10 text-white">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">Wingman</p>
      <p className="text-base font-semibold mt-0.5">Federation Trading</p>
    </div>
    <div className="px-4 mt-4 space-y-2.5">
      {[
        { name: 'Sarah from Studio K', msg: 'Are the cast iron radiators still available?' },
        { name: 'Mike (builder)', msg: 'Can I view the brass taps tomorrow morning?' },
        { name: 'Aria @ Reclaimed', msg: 'B2B trade — interested in the joists.' },
      ].map((m) => (
        <div
          key={m.name}
          className="rounded-xl bg-white/5 border border-white/5 p-3"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-[#D4AF37] font-medium">{m.name}</span>
            <span className="text-[9px] text-slate-500">2m ago</span>
          </div>
          <p className="text-[11px] text-slate-300 leading-snug line-clamp-2">{m.msg}</p>
        </div>
      ))}
    </div>
    <div className="absolute bottom-0 inset-x-0 h-14 bg-[#0B1224] border-t border-white/5 grid grid-cols-3">
      {['Inbox', 'Add', 'Settings'].map((label, i) => (
        <div
          key={label}
          className={`flex flex-col items-center justify-center text-[9px] ${i === 0 ? 'text-[#D4AF37]' : 'text-slate-500'}`}
        >
          <div className={`w-4 h-4 rounded ${i === 0 ? 'bg-[#D4AF37]/30' : 'bg-slate-700/40'} mb-0.5`} />
          {label}
        </div>
      ))}
    </div>
  </div>
);
